import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import QueryTemplateManager from './QueryTemplates/QueryTemplateManager';
import AuthModal from './Auth/AuthModal';
import { Search, ArrowRight } from 'lucide-react';
import ErrorBoundary from './UI/ErrorBoundary';

// Removed unused LoadingSpinner component

// Main component now shows Query Template Manager for authenticated users
const AISearchRankingAnalyzerContent = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen app-background dashboard-optimized">
      {/* Show Query Template Manager for authenticated users, Auth Landing for non-authenticated */}
      {isAuthenticated ? (
        <div className="pt-20 p-8 dashboard-optimized">
          <ErrorBoundary
            title="Query Template Error"
            customMessage="The query template manager encountered an error."
          >
            <QueryTemplateManager />
          </ErrorBoundary>
        </div>
      ) : (
        <AuthLandingPage />
      )}
    </div>
  );
};

// Elegant Two-Column Landing Page
const AuthLandingPage = () => {
  const { isDark } = useTheme();
  const [showAuthModal, setShowAuthModal] = useState(false);

  return (
    <div className="relative h-screen overflow-hidden dashboard-optimized">
      {/* Floating geometric shapes - Reduced for performance */}
      <div className="geometric-shape"></div>
      
      {/* Two Column Layout */}
      <div className="h-full flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center h-full py-20">
            
            {/* Left Column - Hero Content */}
            <div className="flex flex-col justify-center space-y-8 animate-slide-in-up">
              {/* Hero Title */}
              <div className="space-y-6">
                <h1 className={`text-3xl lg:text-5xl font-mono leading-tight text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  Answer Engine Optimization
                </h1>
                
                <p className={`text-lg lg:text-xl leading-relaxed font-sans max-w-xl ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Eyehill's deep analysis tool for answer engine optimization
                </p>
              </div>
              
              {/* CTA Button */}
              <div className="pt-4">
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="btn-glass-accent px-6 py-3 rounded-lg font-semibold text-base font-sans inline-flex items-center justify-center gap-2 hover:scale-105 transform transition-all duration-300"
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              
              {/* Quick Stats */}
              <div className={`flex gap-8 pt-8 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent-400 font-mono">3</div>
                  <div className={`text-sm font-sans ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>AI Engines</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent-400 font-mono">100%</div>
                  <div className={`text-sm font-sans ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Automated</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent-400 font-mono">24/7</div>
                  <div className={`text-sm font-sans ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Analysis</div>
                </div>
              </div>
            </div>
            
            {/* Right Column - Animation Placeholder */}
            <div className="hidden lg:flex items-center justify-center h-full">
              <div className="glass rounded-3xl w-full h-96 flex items-center justify-center animate-fade-in-scale delay-300">
                <div className="text-center space-y-4">
                  <div className="w-24 h-24 mx-auto glass-accent rounded-2xl flex items-center justify-center animate-float">
                    <Search className="w-12 h-12 text-accent-400" />
                  </div>
                  <p className={`font-sans text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Animation Coming Soon
                  </p>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </div>
  );
};

const AISearchRankingAnalyzerRefactored = () => {
  return (
    <ErrorBoundary 
      title="Application Error"
      customMessage="The AI Search Ranking Analyzer encountered an unexpected error."
    >
      <AISearchRankingAnalyzerContent />
    </ErrorBoundary>
  );
};

export default AISearchRankingAnalyzerRefactored;